/**
 * 운영자 — 대행사 목록·상세 (261025-agency-white-label). **읽기 전용**이다:
 * 등록·호스트·직원 계정·경계 이동은 운영자 CLI(`cli agency …`)가 한다(RUNBOOK-admin 「대행사 화이트라벨 온보딩」).
 *
 * 대리 로그인은 계정 화면과 같은 절차(useImpersonate)다. 새 탭은 **liveGPT 주소(/app)** 로 열린다 —
 * 대행사 계정이라도 화면은 대행사 브랜딩이 아니라 liveGPT 모양이다(토큰이 탭의 sessionStorage 라 다른 오리진으로 옮길 수 없다).
 */
import { agency } from '@geo-admin/shared/contract/api/index.ts';
import type { RouteResponse } from '@geo-admin/shared/contract/route.ts';
import { Link, useParams } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../components/data-table.tsx';
import { PageTitle } from '../components/PageTitle.tsx';
import { Button } from '../components/ui/button.tsx';
import { Badge, Card, CardBody } from '../components/ui/card.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi } from '../lib/api.ts';
import { dateTime, relative } from '../lib/format.ts';
import { PATHS } from '../lib/paths.ts';
import { useImpersonate } from '../lib/useImpersonate.ts';

type Agency = RouteResponse<typeof agency.listAgencies>['agencies'][number];
type Detail = RouteResponse<typeof agency.getAgency>;
type Account = Detail['accounts'][number];
type AgencyBrand = Detail['brands'][number];

/** 대행사 도메인 바로가기 — 대표 호스트(먼저 등록한 것)가 첫째다 */
function HostLinks({ hosts }: { hosts: string[] }) {
  if (hosts.length === 0) return <span className="text-slate-400">도메인 없음(화이트라벨 아님)</span>;
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1">
      {hosts.map((h) => (
        <a
          key={h}
          href={`https://${h}/`}
          target="_blank"
          rel="noreferrer"
          className="text-blue-700 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {h} ↗
        </a>
      ))}
    </div>
  );
}

/** 온보딩 준비 상태 — 빠진 것이 있으면 그 기능이 대행사 고객에게 막혀 있다 */
function Readiness({ a }: { a: Agency }) {
  return (
    <div className="flex flex-wrap gap-1">
      <Badge tone={a.emailDomain ? 'green' : 'amber'}>{a.emailDomain ? `메일 ${a.emailDomain}` : '메일 미인증'}</Badge>
      <Badge tone={a.alimtalk ? 'green' : 'amber'}>{a.alimtalk ? '알림톡' : '알림톡 없음'}</Badge>
      <Badge tone={a.legalDocs ? 'green' : 'slate'}>{a.legalDocs ? '약관 게시' : '약관 외부'}</Badge>
    </div>
  );
}

export function AgenciesPage() {
  const list = useApi(agency.listAgencies);
  const columns: ColumnDef<Agency, unknown>[] = [
    {
      header: '대행사',
      accessorKey: 'name',
      cell: ({ row }) => (
        <div>
          <Link to={PATHS.admin.agency} params={{ id: row.original.id }} className="font-medium hover:underline">
            {row.original.name}
          </Link>
          <div className="text-xs text-slate-500">서비스명 {row.original.serviceName}</div>
        </div>
      ),
    },
    { header: '도메인', cell: ({ row }) => <HostLinks hosts={row.original.hosts} /> },
    { header: '브랜드', accessorKey: 'brandCount' },
    {
      header: '계정',
      cell: ({ row }) => `직원 ${row.original.staffCount} · 고객 ${row.original.clientCount}`,
    },
    { header: '준비 상태', cell: ({ row }) => <Readiness a={row.original} /> },
    { header: '등록', cell: ({ row }) => dateTime(row.original.createdAt) },
  ];
  return (
    <div className="space-y-4">
      <PageTitle
        title="대행사"
        description="화이트라벨 대행사 — 등록·도메인·직원 계정은 운영자 CLI(cli agency)로 한다"
      />
      <Card>
        <CardBody>
          {list.isLoading ? (
            <Loading />
          ) : list.error ? (
            <ErrorBox error={list.error} />
          ) : (
            <DataTable
              columns={columns}
              data={list.data?.agencies ?? []}
              empty="대행사가 없습니다"
              rowLink={(r) => ({ to: PATHS.admin.agency, params: { id: r.id } })}
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}

const STATUS_LABEL: Record<AgencyBrand['status'], string> = {
  pending: '승인 대기',
  confirmed: '결제 대기',
  active: '이용 중',
};

export function AgencyDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const detail = useApi(agency.getAgency, { params: { id } });
  const impersonate = useImpersonate();

  if (detail.isLoading) return <Loading />;
  if (detail.error) return <ErrorBox error={detail.error} />;
  const d = detail.data!;
  const a = d.agency;

  const accountColumns: ColumnDef<Account, unknown>[] = [
    {
      header: '계정',
      accessorKey: 'email',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.email}</div>
          {row.original.name ? <div className="text-xs text-slate-500">{row.original.name}</div> : null}
        </div>
      ),
    },
    {
      header: '구분',
      cell: ({ row }) =>
        row.original.role === 'staff' ? <Badge tone="blue">직원</Badge> : <Badge tone="slate">고객</Badge>,
    },
    { header: '브랜드', accessorKey: 'brandCount' },
    {
      header: '마지막 로그인',
      cell: ({ row }) =>
        row.original.mustChangePassword && !row.original.lastLoginAt ? (
          <Badge tone="amber">첫 로그인 전</Badge>
        ) : (
          relative(row.original.lastLoginAt)
        ),
    },
    {
      header: '',
      id: 'actions',
      cell: ({ row }) => (
        <Button size="sm" variant="outline" onClick={() => void impersonate(row.original.id)}>
          대리 로그인
        </Button>
      ),
    },
  ];

  const brandColumns: ColumnDef<AgencyBrand, unknown>[] = [
    {
      header: '브랜드',
      accessorKey: 'name',
      cell: ({ row }) => (
        <Link to={PATHS.admin.brand} params={{ id: row.original.id }} className="font-medium hover:underline">
          {row.original.name}
        </Link>
      ),
    },
    {
      header: '상태',
      cell: ({ row }) => (
        <div className="flex gap-1">
          <Badge tone={row.original.status === 'active' ? 'green' : 'amber'}>{STATUS_LABEL[row.original.status]}</Badge>
          {row.original.isTest ? <Badge tone="slate">테스트</Badge> : null}
        </div>
      ),
    },
    { header: '등록', cell: ({ row }) => dateTime(row.original.createdAt) },
  ];

  const home = a.hosts[0] ? `https://${a.hosts[0]}/` : null;
  return (
    <div className="space-y-4">
      <PageTitle
        title={a.name}
        description={`서비스명 ${a.serviceName}`}
        actions={
          home ? (
            <a href={home} target="_blank" rel="noreferrer">
              <Button variant="outline">대행사 홈페이지 ↗</Button>
            </a>
          ) : null
        }
      />
      <Card>
        <CardBody className="space-y-3 text-sm">
          <div className="grid gap-3 sm:grid-cols-[8rem_1fr]">
            <div className="text-slate-500">도메인</div>
            <HostLinks hosts={a.hosts} />
            <div className="text-slate-500">준비 상태</div>
            <Readiness a={a} />
            {home ? (
              <>
                <div className="text-slate-500">대행사 로그인</div>
                <a href={`${home}login`} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
                  {`${home}login`} ↗
                </a>
              </>
            ) : null}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-3">
          <h2 className="font-semibold">계정</h2>
          <p className="text-xs text-slate-500">
            대리 로그인은 새 탭에서 liveGPT 주소로 열립니다 — 데이터·권한은 그 계정 그대로이고 화면 모양만 liveGPT
            입니다.
          </p>
          <DataTable columns={accountColumns} data={d.accounts} empty="계정이 없습니다 — cli agency staff-add" />
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-3">
          <h2 className="font-semibold">브랜드</h2>
          <DataTable columns={brandColumns} data={d.brands} empty="브랜드가 없습니다" />
        </CardBody>
      </Card>
    </div>
  );
}
